import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Users, TrendingUp, Heart, Zap } from 'lucide-react';
import Header from '../components/Header';

const ThankYouPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-red-100 to-red-200 rounded-full opacity-20 animate-float"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-gray-100 to-gray-200 rounded-full opacity-30 animate-bounce-subtle"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-red-600" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-black mb-6">
              Thank You!
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 font-medium">
              Your Zylu Growth Demo has been scheduled successfully. We're excited to help transform your salon business!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200 mb-8"
          >
            <h2 className="text-2xl font-bold text-black mb-4">What Happens Next?</h2>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">1</span>
                </div>
                <h3 className="font-semibold text-black mb-2">Confirmation Email</h3>
                <p className="text-gray-600 text-sm">You'll receive a confirmation email with meeting details and calendar invite</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">2</span>
                </div>
                <h3 className="font-semibold text-black mb-2">Demo Preparation</h3>
                <p className="text-gray-600 text-sm">We'll send you a reminder and a link to join the demo.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-red-600">3</span>
                </div>
                <h3 className="font-semibold text-black mb-2">Live Growth Demo</h3>
                <p className="text-gray-600 text-sm">Discover how Zylu can accelerate your salon's growth.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/"
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center justify-center"
            >
              Back to Home
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
            <a
              href="https://zylu.co/pricing-salon-growth-management-software/"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
            >
              View Pricing Plans
            </a>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              Why Salon Owners Choose Zylu
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Join thousands of salon owners who have transformed their business with our automation platform
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-red-600" />
                <h3 className="text-xl font-bold text-black mb-2">28%+ Revenue Boost</h3>
                <p className="text-gray-600">Automated systems that drive consistent growth</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <Users className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-xl font-bold text-black mb-2">4-5x Client Retention</h3>
                <p className="text-gray-600">Build lasting relationships with automated follow-ups</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <Zap className="h-12 w-12 mx-auto mb-4 text-red-600" />
                <h3 className="text-xl font-bold text-black mb-2">15hrs Saved Weekly</h3>
                <p className="text-gray-600">Focus on what matters most - your clients</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                <Heart className="h-12 w-12 mx-auto mb-4 text-black" />
                <h3 className="text-xl font-bold text-black mb-2">98% Satisfaction</h3>
                <p className="text-gray-600">Join our community of happy salon owners</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              While you wait for your demo, explore our platform and see how Zylu can transform your salon business
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://zylu.co/pricing-salon-growth-management-software/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-red-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
              >
                Explore Pricing Plans
                <ArrowRight className="h-5 w-5 ml-2" />
              </a>
              <Link
                to="/"
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
              >
                Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 text-black py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img src={`${window.location.origin}/logo-zylu.webp`} alt="Zylu Logo" className="h-10 bg-black" />
            </div>
            <p className="text-gray-600 mb-6">
              Empowering salon owners to run their business on autopilot
            </p>
            <div className="flex justify-center space-x-8 text-sm text-gray-600">
              <a href="#" className="hover:text-red-600 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-red-600 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-red-600 transition-colors">Contact Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage;
